import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminAiCostPage.css";

type ModelPrice={
  modelKey:string;displayName:string;inputPerMillionUsd:number;cachedInputPerMillionUsd:number;
  outputPerMillionUsd:number;pricingNote?:string|null;effectiveFrom:string;
};
type Budget={
  monthlyBudgetUsd:number;warningRatio:number;autoDowngradeRatio:number;hardStopRatio:number;
  autoDowngradeEnabled:boolean;hardStopEnabled:boolean;fxKrwPerUsd:number;usedRatio:number;remainingUsd:number;
};
type Summary={calls:number;inputTokens:number;cachedInputTokens:number;outputTokens:number;costUsd:number;costKrw:number;projectedCostUsd:number;projectedCostKrw:number};
type FeatureRow={
  featureKey:string;surface:string;label:string;preferredModel:string;economyModel:string;enabled:boolean;
  calls:number;inputTokens:number;outputTokens:number;costUsd:number;costKrw:number;
};
type Dashboard={
  month:string;pricing:{models:ModelPrice[]};budget:Budget;summary:Summary;today:{calls:number;costUsd:number;costKrw:number};
  daily:Array<{date:string;calls:number;costUsd:number;costKrw:number}>;
  byFeature:FeatureRow[];
  byModel:Array<{modelKey:string;calls:number;inputTokens:number;cachedInputTokens:number;outputTokens:number;costUsd:number}>;
  byUser:Array<{personId?:string|null;displayName:string;email?:string|null;calls:number;costUsd:number;costKrw:number}>;
};

function moneyKrw(value:number){return new Intl.NumberFormat("ko-KR",{maximumFractionDigits:0}).format(Number(value||0))+"원";}
function moneyUsd(value:number){return "$"+Number(value||0).toFixed(value<1?4:2);}
function tokens(value:number){
  const n=Number(value||0); if(n>=1_000_000)return (n/1_000_000).toFixed(2)+"M"; if(n>=1000)return (n/1000).toFixed(1)+"K"; return String(n);
}
function pct(value:number){return Math.round(Number(value||0)*100)+"%";}

export function AdminAiCostPage({client}:{client:PlatformClient}){
  const now=new Date();
  const [month,setMonth]=useState(now.toISOString().slice(0,7));
  const [data,setData]=useState<Dashboard|null>(null);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [budgetForm,setBudgetForm]=useState({
    monthlyBudgetUsd:"200",warningRatio:"70",autoDowngradeRatio:"80",hardStopRatio:"100",
    autoDowngradeEnabled:true,hardStopEnabled:false,fxKrwPerUsd:"1357"
  });

  async function load(){
    setError("");
    const {data,error}=await client.rpc("admin_ai_cost_dashboard",{p_month:month+"-01"});
    if(error){setError(error.message);return;}
    const next=data as Dashboard;
    setData(next);
    setBudgetForm({
      monthlyBudgetUsd:String(next.budget.monthlyBudgetUsd),
      warningRatio:String(Math.round(next.budget.warningRatio*100)),
      autoDowngradeRatio:String(Math.round(next.budget.autoDowngradeRatio*100)),
      hardStopRatio:String(Math.round(next.budget.hardStopRatio*100)),
      autoDowngradeEnabled:next.budget.autoDowngradeEnabled,
      hardStopEnabled:next.budget.hardStopEnabled,
      fxKrwPerUsd:String(next.budget.fxKrwPerUsd)
    });
  }

  useEffect(()=>{void load();},[client,month]);

  const budgetStatus=useMemo(()=>{
    if(!data)return "NORMAL";
    if(data.budget.usedRatio>=data.budget.hardStopRatio)return "LIMIT";
    if(data.budget.usedRatio>=data.budget.autoDowngradeRatio)return "ECONOMY";
    if(data.budget.usedRatio>=data.budget.warningRatio)return "WARNING";
    return "NORMAL";
  },[data]);

  async function saveBudget(){
    setBusy("budget");setError("");setNotice("");
    const {data,error}=await client.rpc("admin_update_ai_budget_policy",{
      p_monthly_budget_usd:Number(budgetForm.monthlyBudgetUsd),
      p_warning_ratio:Number(budgetForm.warningRatio)/100,
      p_auto_downgrade_ratio:Number(budgetForm.autoDowngradeRatio)/100,
      p_hard_stop_ratio:Number(budgetForm.hardStopRatio)/100,
      p_auto_downgrade_enabled:budgetForm.autoDowngradeEnabled,
      p_hard_stop_enabled:budgetForm.hardStopEnabled,
      p_fx_krw_per_usd:Number(budgetForm.fxKrwPerUsd)
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setData(data as Dashboard);setNotice("AI 예산 정책을 저장했습니다.");
  }

  async function saveFeature(row:FeatureRow,changes:Partial<FeatureRow>){
    const next={...row,...changes};
    setBusy(row.featureKey);setError("");setNotice("");
    const {data,error}=await client.rpc("admin_update_ai_feature_policy",{
      p_feature_key:row.featureKey,
      p_preferred_model:next.preferredModel,
      p_economy_model:next.economyModel,
      p_enabled:next.enabled
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setData(data as Dashboard);setNotice(next.label+" 모델 정책을 저장했습니다.");
  }

  if(!data && !error)return <p className="muted">AI 비용 관제를 불러오는 중…</p>;

  const maxDaily=Math.max(0.000001,...(data?.daily||[]).map((row)=>Number(row.costUsd||0)));

  return (
    <section className="admin-ai-cost-page">
      <div className="section-heading">
        <div><p className="eyebrow">AI FINOPS · COST CONTROL</p><h2>AI 비용 관제</h2><p className="route-description">Wallet·Center·Admin AI 사용량과 모델 비용을 한곳에서 보고 예산·자동전환 정책을 관리합니다.</p></div>
        <label className="ai-cost-month">조회 월<input type="month" value={month} onChange={(e)=>setMonth(e.target.value)}/></label>
      </div>

      {error&&<p className="error">{error}</p>}
      {notice&&<p className="teacher-success">{notice}</p>}

      {data&&<>
        <section className={"ai-budget-hero "+budgetStatus.toLowerCase()}>
          <div>
            <p className="eyebrow">MONTHLY BUDGET</p>
            <strong>{moneyKrw(data.summary.costKrw)}</strong>
            <span>{moneyUsd(data.summary.costUsd)} 사용 · 예산 {moneyUsd(data.budget.monthlyBudgetUsd)}</span>
          </div>
          <div className="ai-budget-progress">
            <div><i style={{width:Math.min(100,data.budget.usedRatio*100)+"%"}}/></div>
            <p><span>사용 {pct(data.budget.usedRatio)}</span><span>잔여 {moneyUsd(data.budget.remainingUsd)}</span></p>
          </div>
          <div className="ai-budget-state">
            <b>{budgetStatus==="ECONOMY"?"절약모델 자동전환":budgetStatus==="WARNING"?"예산 경고":budgetStatus==="LIMIT"?"예산 한도 도달":"정상 운영"}</b>
            <small>월말 예상 {moneyKrw(data.summary.projectedCostKrw)} · {moneyUsd(data.summary.projectedCostUsd)}</small>
          </div>
        </section>

        <div className="ai-cost-kpis">
          <Metric label="오늘 AI 호출" value={String(data.today.calls)} sub={moneyKrw(data.today.costKrw)}/>
          <Metric label="이번 달 호출" value={String(data.summary.calls)} sub={moneyKrw(data.summary.costKrw)}/>
          <Metric label="입력 토큰" value={tokens(data.summary.inputTokens)} sub={"캐시 "+tokens(data.summary.cachedInputTokens)}/>
          <Metric label="출력 토큰" value={tokens(data.summary.outputTokens)} sub={"월말 예상 "+moneyUsd(data.summary.projectedCostUsd)}/>
        </div>

        <div className="ai-cost-main-grid">
          <section className="ai-cost-card">
            <div className="panel-title"><strong>일별 비용</strong><span>{data.month}</span></div>
            <div className="ai-daily-chart">
              {data.daily.length?data.daily.map((row)=>(
                <div className="ai-daily-row" key={row.date}>
                  <span>{row.date.slice(5)}</span>
                  <div><i style={{width:(Number(row.costUsd)/maxDaily*100)+"%"}}/></div>
                  <b>{moneyKrw(row.costKrw)}</b><small>{row.calls}회</small>
                </div>
              )):<p className="case-empty">아직 기록된 AI 호출이 없습니다.</p>}
            </div>
          </section>

          <section className="ai-cost-card">
            <div className="panel-title"><strong>기능별 비용</strong><span>실제 사용량</span></div>
            <div className="ai-feature-cost-list">
              {data.byFeature.map((row)=>(
                <div key={row.featureKey}>
                  <span><b>{row.surface}</b><strong>{row.label}</strong><small>{row.preferredModel}{row.economyModel!==row.preferredModel?" → 절약 "+row.economyModel:""}</small></span>
                  <span><strong>{moneyKrw(row.costKrw)}</strong><small>{row.calls}회 · {tokens(row.inputTokens+row.outputTokens)}</small></span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="ai-cost-card ai-policy-card">
          <div className="panel-title"><strong>월 예산 정책</strong><span>초기값: 하드스톱 OFF</span></div>
          <div className="ai-policy-grid">
            <label>월 예산 USD<input type="number" min="1" step="1" value={budgetForm.monthlyBudgetUsd} onChange={(e)=>setBudgetForm(v=>({...v,monthlyBudgetUsd:e.target.value}))}/></label>
            <label>경고선 %<input type="number" min="1" max="100" value={budgetForm.warningRatio} onChange={(e)=>setBudgetForm(v=>({...v,warningRatio:e.target.value}))}/></label>
            <label>절약모델 전환 %<input type="number" min="1" max="100" value={budgetForm.autoDowngradeRatio} onChange={(e)=>setBudgetForm(v=>({...v,autoDowngradeRatio:e.target.value}))}/></label>
            <label>하드스톱 %<input type="number" min="1" max="200" value={budgetForm.hardStopRatio} onChange={(e)=>setBudgetForm(v=>({...v,hardStopRatio:e.target.value}))}/></label>
            <label>USD→KRW 표시환율<input type="number" min="1" step="0.01" value={budgetForm.fxKrwPerUsd} onChange={(e)=>setBudgetForm(v=>({...v,fxKrwPerUsd:e.target.value}))}/></label>
          </div>
          <div className="ai-policy-toggles">
            <label><input type="checkbox" checked={budgetForm.autoDowngradeEnabled} onChange={(e)=>setBudgetForm(v=>({...v,autoDowngradeEnabled:e.target.checked}))}/><span><strong>예산 임계치에서 절약모델 자동전환</strong><small>기능별 economy model로 자동 변경</small></span></label>
            <label className="danger"><input type="checkbox" checked={budgetForm.hardStopEnabled} onChange={(e)=>setBudgetForm(v=>({...v,hardStopEnabled:e.target.checked}))}/><span><strong>예산 초과 시 AI 호출 중단</strong><small>ON일 때만 hard stop 적용</small></span></label>
          </div>
          <button className="primary" disabled={busy==="budget"} onClick={()=>void saveBudget()}>{busy==="budget"?"저장 중…":"예산 정책 저장"}</button>
        </section>

        <section className="ai-cost-card">
          <div className="panel-title"><strong>기능별 모델 정책</strong><span>예산 80% 도달 시 절약모델</span></div>
          <div className="ai-model-policy-list">
            {data.byFeature.map((row)=>(
              <FeaturePolicy key={row.featureKey} row={row} models={data.pricing.models} busy={busy===row.featureKey} onSave={saveFeature}/>
            ))}
          </div>
        </section>

        <div className="ai-cost-main-grid">
          <section className="ai-cost-card">
            <div className="panel-title"><strong>사용자별 호출</strong><span>상위 30명</span></div>
            <div className="ai-user-table">
              {data.byUser.length?data.byUser.map((row,index)=>(
                <div key={(row.personId||row.displayName)+String(index)}><span><strong>{row.displayName}</strong><small>{row.email||"-"}</small></span><b>{row.calls}회</b><strong>{moneyKrw(row.costKrw)}</strong></div>
              )):<p className="case-empty">사용자별 AI 사용 기록이 없습니다.</p>}
            </div>
          </section>
          <section className="ai-cost-card">
            <div className="panel-title"><strong>모델별 사용</strong><span>OpenAI API 추정비용</span></div>
            <div className="ai-user-table">
              {data.byModel.length?data.byModel.map((row)=>(
                <div key={row.modelKey}><span><strong>{row.modelKey}</strong><small>입력 {tokens(row.inputTokens)} · 출력 {tokens(row.outputTokens)}</small></span><b>{row.calls}회</b><strong>{moneyUsd(row.costUsd)}</strong></div>
              )):<p className="case-empty">모델 사용 기록이 없습니다.</p>}
            </div>
          </section>
        </div>

        <section className="ai-cost-card ai-price-catalog">
          <div className="panel-title"><strong>가격 기준</strong><span>토큰 100만 개당 USD</span></div>
          <div className="ai-price-grid">
            {data.pricing.models.filter((m)=>m.modelKey!=="gpt-5.6").map((m)=>(
              <article key={m.modelKey}><strong>{m.displayName}</strong><span>입력 {moneyUsd(m.inputPerMillionUsd)}</span><span>캐시 {moneyUsd(m.cachedInputPerMillionUsd)}</span><span>출력 {moneyUsd(m.outputPerMillionUsd)}</span><small>{m.pricingNote}</small></article>
            ))}
          </div>
          <p className="ai-cost-footnote">표시 비용은 OpenAI 응답의 실제 token usage × 등록 단가로 계산한 추정치입니다. 세금·환율·향후 가격 변경은 실제 청구액과 차이를 만들 수 있습니다.</p>
        </section>
      </>}
    </section>
  );
}

function Metric({label,value,sub}:{label:string;value:string;sub:string}){
  return <div className="ai-cost-metric"><span>{label}</span><strong>{value}</strong><small>{sub}</small></div>;
}

function FeaturePolicy({row,models,busy,onSave}:{row:FeatureRow;models:ModelPrice[];busy:boolean;onSave:(row:FeatureRow,changes:Partial<FeatureRow>)=>Promise<void>}){
  const [preferred,setPreferred]=useState(row.preferredModel);
  const [economy,setEconomy]=useState(row.economyModel);
  const [enabled,setEnabled]=useState(row.enabled);
  useEffect(()=>{setPreferred(row.preferredModel);setEconomy(row.economyModel);setEnabled(row.enabled);},[row]);
  return <div className="ai-model-policy-row">
    <span><b>{row.surface}</b><strong>{row.label}</strong><small>{row.calls}회 · {moneyKrw(row.costKrw)}</small></span>
    <label>주모델<select value={preferred} onChange={(e)=>setPreferred(e.target.value)}>{models.filter(m=>m.modelKey!=="gpt-5.6").map(m=><option key={m.modelKey} value={m.modelKey}>{m.displayName}</option>)}</select></label>
    <label>절약모델<select value={economy} onChange={(e)=>setEconomy(e.target.value)}>{models.filter(m=>m.modelKey!=="gpt-5.6").map(m=><option key={m.modelKey} value={m.modelKey}>{m.displayName}</option>)}</select></label>
    <label className="ai-feature-enabled"><input type="checkbox" checked={enabled} onChange={(e)=>setEnabled(e.target.checked)}/>사용</label>
    <button className="secondary" disabled={busy} onClick={()=>void onSave(row,{preferredModel:preferred,economyModel:economy,enabled})}>{busy?"저장 중":"저장"}</button>
  </div>;
}
